import React, { lazy, Suspense } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';
import { RoutePermissionGuard } from '@shared/auth/RoutePermissionGuard';
import { Role } from '@shared/auth/AuthContext';

const BrandingPage = lazy(() => import('./modules/branding/pages/BrandingPage'));
const SuitesManagerPage = lazy(() => import('./modules/suites-manager/pages/SuitesManagerPage'));
const SkillsTaxonomyPage = lazy(() => import('./modules/taxonomy/pages/SkillsTaxonomyPage'));
const ApiKeysPage = lazy(() => import('./modules/security/pages/ApiKeysPage'));
const AuditLogsPage = lazy(() => import('./modules/security/pages/AuditLogsPage'));
const ImportExportPage = lazy(() => import('./modules/portability/pages/ImportExportPage'));
const AccessControlMatrixPage = lazy(() => import('./modules/access-control/AccessControlMatrixPage'));

const LoadingFallback: React.FC = () => (
  <div className="flex h-64 items-center justify-center">
    <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const PLATFORM_ROLES: Role[] = ['owner', 'admin'];

export const platformRoutes: RouteObject[] = [
  {
    index: true,
    element: <Navigate to="branding" replace />,
  },
  {
    path: 'branding',
    handle: { crumb: 'Branding & Domain' },
    element: (
      <RoutePermissionGuard permission="settings.manage" allowedRoles={PLATFORM_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <BrandingPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'skills',
    handle: { crumb: 'Skills & Taxonomy' },
    element: (
      <RoutePermissionGuard permission="taxonomy.manage" allowedRoles={PLATFORM_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <SkillsTaxonomyPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'suites',
    handle: { crumb: 'Suite Marketplace' },
    element: (
      <RoutePermissionGuard permission="settings.manage" allowedRoles={PLATFORM_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <SuitesManagerPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'api-keys',
    handle: { crumb: 'API Keys & Webhooks' },
    element: (
      <RoutePermissionGuard permission="settings.manage" allowedRoles={PLATFORM_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <ApiKeysPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'access-control',
    handle: { crumb: 'Access Control Matrix' },
    element: (
      <RoutePermissionGuard permission="settings.manage" allowedRoles={PLATFORM_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <AccessControlMatrixPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'audit-logs',
    handle: { crumb: 'Platform Audit Logs' },
    element: (
      <RoutePermissionGuard permission="audit.read" allowedRoles={PLATFORM_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <AuditLogsPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'import-export',
    handle: { crumb: 'Data Portability & Import' },
    element: (
      <RoutePermissionGuard permission="settings.manage" allowedRoles={PLATFORM_ROLES}>
        <Suspense fallback={<LoadingFallback />}>
          <ImportExportPage />
        </Suspense>
      </RoutePermissionGuard>
    ),
  },
];

export default platformRoutes;
